/**
 * The tab's demo persona (19 Oct), kept in sessionStorage.
 *
 * sessionStorage because it is per TAB: each tab of one browser plays its own
 * role, which a cookie cannot do. A reload keeps it; closing the tab forgets
 * it. Read by the API client on every request (outside React), and by the
 * banner through {@link useDemoPersona}, which re-renders when it changes.
 */

import { useSyncExternalStore } from "react";

import { logger } from "@/lib/logger";

import { type DemoPersona, parseDemoPersona } from "./demo-persona";

const STORAGE_KEY = "helpdesk.demoPersona";

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch (error) {
    logger.debug("[demo] sessionStorage unavailable:", error);
    return null;
  }
}

/* Cached so `useSyncExternalStore` gets the same object until it changes. */
let current: DemoPersona | null = parseDemoPersona(storage()?.getItem(STORAGE_KEY) ?? null);
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** The tab's persona, or null when the tab is the admin themselves. */
export function getDemoPersona(): DemoPersona | null {
  // Run out since it was stored: the server stops honouring it, so stop sending it.
  if (current !== null && Date.parse(current.expires_at) <= Date.now()) {
    return null;
  }
  return current;
}

/** Play this persona in this tab. */
export function setDemoPersona(persona: DemoPersona): void {
  current = persona;
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(persona));
  } catch (error) {
    logger.warn("[demo] could not store the persona", error);
  }
  logger.debug("[demo] persona set", persona.role);
  emit();
}

/** Back to me: "Back to me", sign-out, or the server saying the persona ended. */
export function clearDemoPersona(): void {
  if (current === null && storage()?.getItem(STORAGE_KEY) == null) {
    return;
  }
  current = null;
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.debug("[demo] could not clear the stored persona", error);
  }
  logger.debug("[demo] persona cleared");
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The tab's persona, re-rendering when it changes. */
export function useDemoPersona(): DemoPersona | null {
  return useSyncExternalStore(subscribe, getDemoPersona, getDemoPersona);
}
